/**
 * BETA — INSTANCE vente lifecycle + card custody. The store keeps
 * { list, custodies } for instances and mirrors each new instance into
 * the unified store.ventes array so the global vente list also sees them.
 */
import { httpError } from "../router.js";
import { paginate, newId, nowIso, todayYmd, money, fefoDeduct } from "./_utils.js";

export function register(route) {
  route("GET", "/instances", (ctx) => {
    const { params, store } = ctx;
    const list = store.data.instances?.list || [];
    const statut = params.statut || "unpaid";
    let rows =
      statut === "paid"
        ? list.filter((i) => i.statut_paiement === "PAYE")
        : statut === "all"
          ? [...list]
          : list.filter((i) => i.statut_paiement !== "PAYE");
    rows.sort((a, b) => (a.date_vente > b.date_vente ? -1 : 1));
    const unpaid = list.filter((i) => i.statut_paiement !== "PAYE");
    const summary = {
      total_unpaid_count: unpaid.length,
      total_unpaid_amount: money(unpaid.reduce((s, i) => s + (i.montant_a_payer || 0), 0)),
    };
    return paginate(rows, params, { summary });
  });

  route("POST", "/instances", (ctx) => {
    const body = ctx.body || {};
    if (!body.client_id) httpError(400, "Client requis pour une instance", "CLIENT_REQUIRED");
    const client = ctx.store.data.clients.find((c) => c.id === body.client_id);
    if (!client) httpError(404, "Client introuvable", "NOT_FOUND_CLIENT");
    const caissier =
      ctx.store.data.users.find((u) => u.role === "CAISSIER") ||
      ctx.store.data.users[0];
    const id = newId("instance-new");
    const num = `INS-2026-${id.slice(-3).toUpperCase()}`;
    const details = (body.details || []).map((it, i) => {
      const p = ctx.store.data.produits.find((x) => x.id === it.produit_id);
      const qte = Number(it.quantite) || 1;
      const pu = Number(it.prix_unitaire ?? p?.ppa) || 0;
      const ttc = money(qte * pu);
      return {
        id: `${id}-det-${i + 1}`,
        produit_id: p?.id,
        produit: p ? { id: p.id, nom_commercial: p.nom_commercial, ppa: p.ppa, dci: p.dci, forme_pharma: p.forme_pharma, dosage: p.dosage } : null,
        stock_lot_id: it.stock_lot_id || null,
        quantite: qte,
        prix_unitaire: pu,
        remise_pct: 0,
        tva_pct: p?.tva_pct ?? 0,
        montant_ligne_ttc: ttc,
        montant_pec_chifa: 0,
        montant_patient: ttc,
      };
    });
    const totalTtc = money(details.reduce((s, d) => s + d.montant_ligne_ttc, 0));

    let custodyId = null;
    if (body.carte_chifa_retenue && client.carte_chifa) {
      custodyId = newId("custody");
      ctx.store.data.instances.custodies.unshift({
        id: custodyId,
        client_id: client.id,
        client: { id: client.id, nom: client.nom, prenom: client.prenom, telephone: client.telephone },
        carte_numero: client.carte_chifa,
        vente_id: id,
        date_retention: todayYmd(),
        date_restitution: null,
        statut: "HELD",
        notes: body.notes || "",
      });
    }

    const instance = {
      id,
      numero_interne: num,
      type_vente: "INSTANCE",
      statut: "VALIDEE",
      date_vente: nowIso(),
      client_id: client.id,
      client: { id: client.id, nom: client.nom, prenom: client.prenom, telephone: client.telephone, carte_chifa: client.carte_chifa },
      caissier_id: caissier?.id ?? null,
      caissier: caissier ? { id: caissier.id, nom: caissier.nom, prenom: caissier.prenom } : null,
      caisse_id: body.caisse_id || "caisse-001",
      montant_total_ht: money(totalTtc / 1.19),
      montant_total_tva: money(totalTtc - totalTtc / 1.19),
      montant_total_ttc: totalTtc,
      montant_pec_chifa: 0,
      montant_a_payer: totalTtc,
      statut_paiement: "NON_PAYE",
      date_paiement: null,
      carte_chifa_retenue: !!body.carte_chifa_retenue,
      carte_numero: body.carte_chifa_retenue ? client.carte_chifa : null,
      custody_id: custodyId,
      mode_paiement: "INSTANCE",
      notes: body.notes || "",
      details,
      created_at: nowIso(),
      updated_at: nowIso(),
    };

    // FEFO-deduct stock
    for (const d of details) fefoDeduct(ctx.store, d.produit_id, d.quantite);

    ctx.store.data.instances.list.unshift(instance);
    ctx.store.data.ventes.unshift(instance);
    ctx.commit();
    return instance;
  });

  route("POST", "/instances/:id/mark-paid", (ctx) => {
    const id = ctx.params.id;
    const inst = ctx.store.data.instances.list.find((x) => x.id === id);
    if (!inst) httpError(404, "Instance introuvable", "NOT_FOUND");
    inst.statut_paiement = "PAYE";
    inst.date_paiement = todayYmd();
    inst.updated_at = nowIso();
    const mirror = ctx.store.data.ventes.find((x) => x.id === id);
    if (mirror) {
      mirror.statut_paiement = "PAYE";
      mirror.date_paiement = inst.date_paiement;
      mirror.updated_at = inst.updated_at;
    }
    ctx.commit();
    return inst;
  });

  route("POST", "/instances/return-card", (ctx) => {
    const custodyId = ctx.body?.custody_id;
    const cu = ctx.store.data.instances.custodies.find((x) => x.id === custodyId);
    if (!cu) httpError(404, "Carte introuvable", "NOT_FOUND_CUSTODY");
    const inst = ctx.store.data.instances.list.find((x) => x.id === cu.vente_id);
    if (inst && inst.statut_paiement !== "PAYE") {
      httpError(400, "Réglez d'abord la vente avant de restituer la carte", "VENTE_UNPAID");
    }
    cu.statut = "RETURNED";
    cu.date_restitution = todayYmd();
    cu.notes = ctx.body?.notes || cu.notes;
    ctx.commit();
    return cu;
  });

  route("GET", "/instances/custodies", (ctx) => {
    const { params, store } = ctx;
    let rows = [...(store.data.instances?.custodies || [])];
    if (params.statut) rows = rows.filter((c) => c.statut === params.statut);
    rows.sort((a, b) => (a.date_retention > b.date_retention ? -1 : 1));
    return paginate(rows, params);
  });
}
